import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import type {
    ActivityInterface,
    CountryInterface,
    DeviceInterface,
    FileInterface,
    ForeignLanguagesInterface,
    MobileLinkInterface,
    MovingVehicleTypeInterface,
    PetTypeInterface,
    ReportingInterface,
    ScholarLevelInterface,
    ScholarSubjectInterface,
    SurfaceTypeInterface,
    VehicleTypeInterface,
    ReferenceInterface,
} from "types/reference";
import { useActivities } from "./activity";
import { useCountries } from "./country";
import { useDevices } from "./device";
import { useFiles } from "./file";
import { useForeignLanguages } from "./foreignLanguages";
import { useMobileLinks } from "./mobileLink";
import { useMovingVehicleTypes } from "./movingVehicleType";
import { usePetTypes } from "./petType";
import { useReporting } from "./reporting";
import { useScholarLevels } from "./scholarLevel";
import { useScholarSubjects } from "./scholarSubject";
import { useSurfaceTypes } from "./surfaceType";
import { useVehicleTypes } from "./vehicleType";

interface Option {
    value: string | number;
    label: string;
}

export interface ReferenceMap {
    device: DeviceInterface;
    pet_type: PetTypeInterface;
    scholar_level: ScholarLevelInterface;
    scholar_subject: ScholarSubjectInterface;
    surface_type: SurfaceTypeInterface;
    moving_vehicle_type: MovingVehicleTypeInterface;
    vehicle_type: VehicleTypeInterface;
    file: FileInterface;
    reporting: ReportingInterface;
    mobile_link: MobileLinkInterface;
    foreign_languages: ForeignLanguagesInterface;
    activity: ActivityInterface;
    countries: CountryInterface;
}

export interface ReferencesHook {
    all<T extends keyof ReferenceMap>(type: T): Array<ReferenceMap[T]>;
    selectList<T extends keyof ReferenceMap>(type: T): Array<Option>;
    get<T extends keyof ReferenceMap>(type: T, code: string): ReferenceMap[T] | undefined;
    refreshData(type?: keyof ReferenceMap): void;
    isLoading: boolean;
}

const emptyList = <T extends ReferenceInterface>() => [] as Array<T>;

const byDisplayOrder = <T extends ReferenceInterface>(a: T, b: T): number => {
    const orderDiff = Number(a.display_order) - Number(b.display_order);
    if (orderDiff !== 0) {
        return orderDiff;
    }
    return String(a.code).localeCompare(String(b.code));
};

export const useReferences = (): ReferencesHook => {
    const { locale } = useIntl();
    const queryClient = useQueryClient();

    const devices = useDevices(locale);
    const petTypes = usePetTypes(locale);
    const scholarLevels = useScholarLevels(locale);
    const scholarSubjects = useScholarSubjects(locale);
    const surfaceTypes = useSurfaceTypes(locale);
    const movingVehicleTypes = useMovingVehicleTypes(locale);
    const vehicleTypes = useVehicleTypes(locale);
    const files = useFiles(locale);
    const reporting = useReporting(locale);
    const mobileLinks = useMobileLinks(locale);
    const foreignLanguages = useForeignLanguages(locale);
    const activities = useActivities(locale);
    const countries = useCountries(locale);

    const references = useMemo(
        () => ({
            device: devices.data ?? emptyList<DeviceInterface>(),
            pet_type: petTypes.data ?? emptyList<PetTypeInterface>(),
            scholar_level: scholarLevels.data ?? emptyList<ScholarLevelInterface>(),
            scholar_subject: scholarSubjects.data ?? emptyList<ScholarSubjectInterface>(),
            surface_type: surfaceTypes.data ?? emptyList<SurfaceTypeInterface>(),
            moving_vehicle_type: movingVehicleTypes.data ?? emptyList<MovingVehicleTypeInterface>(),
            vehicle_type: vehicleTypes.data ?? emptyList<VehicleTypeInterface>(),
            file: files.data ?? emptyList<FileInterface>(),
            reporting: reporting.data ?? emptyList<ReportingInterface>(),
            mobile_link: mobileLinks.data ?? emptyList<MobileLinkInterface>(),
            foreign_languages: foreignLanguages.data ?? emptyList<ForeignLanguagesInterface>(),
            activity: activities.data ?? emptyList<ActivityInterface>(),
            countries: countries.data ?? emptyList<CountryInterface>(),
        }),
        [
            devices.data,
            petTypes.data,
            scholarLevels.data,
            scholarSubjects.data,
            surfaceTypes.data,
            movingVehicleTypes.data,
            vehicleTypes.data,
            files.data,
            reporting.data,
            mobileLinks.data,
            foreignLanguages.data,
            activities.data,
            countries.data,
        ]
    );

    const all = useCallback(
        <T extends keyof ReferenceMap>(type: T): Array<ReferenceMap[T]> =>
            [...references[type]].sort(byDisplayOrder),
        [references]
    );

    const get = useCallback(
        <T extends keyof ReferenceMap>(type: T, code: string): ReferenceMap[T] | undefined =>
            references[type].find((value) => code && value.code.toString() === code.toString()),
        [references]
    );

    const selectList = useCallback(
        <T extends keyof ReferenceMap>(type: T): Array<Option> =>
            [...references[type]]
                .filter((value) => !value.deleted_at)
                .sort(byDisplayOrder)
                .map((value) => ({
                    label: value.value !== undefined ? value.value : value.code,
                    value: value.code,
                })),
        [references]
    );

    const refreshData = useCallback(
        (type?: keyof ReferenceMap) => {
            if (type) {
                queryClient.invalidateQueries({ queryKey: ["reference"] });
                return;
            }
            queryClient.invalidateQueries({ queryKey: ["reference"] });
        },
        [queryClient]
    );

    const isLoading =
        devices.isLoading ||
        petTypes.isLoading ||
        scholarLevels.isLoading ||
        scholarSubjects.isLoading ||
        surfaceTypes.isLoading ||
        movingVehicleTypes.isLoading ||
        vehicleTypes.isLoading ||
        files.isLoading ||
        reporting.isLoading ||
        mobileLinks.isLoading ||
        foreignLanguages.isLoading ||
        activities.isLoading ||
        countries.isLoading;

    return { all, selectList, get, refreshData, isLoading };
};

export type getReferenceReturn = ReferenceMap;
export type ReferencesProviderInterface = Omit<ReferencesHook, "isLoading">;
